import {
  ColDef,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import { useCallback, useState } from 'react'
import { useBulkSheetState } from '../bulkSheetState/bulkSheetState'
import { SortedColumnState } from '../../../../model/bulkSheetColumnSortState'

export const useColumnState = ({
  gridApi,
  autoGroupColumnDef,
  bulkSheetState,
}: {
  gridApi: GridApi | undefined
  autoGroupColumnDef?: ColDef
  bulkSheetState: ReturnType<typeof useBulkSheetState>
}): {
  filteredColumns: ColDef[]
  sortColumnsState: SortedColumnState[]
  rememberColumnState: () => void
  resetColumnState: () => void
  onColumnVisible: (e: ColumnVisibleEvent) => void
  onFilterChanged: (e: FilterChangedEvent) => void
  onDeleteFilteredColumn: (column: ColDef) => void
  resetFilters: () => void
  onSortChanged: (e: SortChangedEvent) => void
  onDeleteSortedColumn: (colId: string | ColDef<any>) => void
  onDeleteSortedAllColumns: () => void
  onChangeSortColumnState: (
    colId: string | ColDef<any>,
    sort: 'asc' | 'desc' | null
  ) => void
} => {
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi?.getColumnState()
    const autoColumnState = columnState?.find(
      c => c.colId === 'ag-Grid-AutoColumn'
    )
    if (autoColumnState && autoGroupColumnDef) {
      autoGroupColumnDef.width = autoColumnState?.width ?? undefined
    }
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState, autoGroupColumnDef, gridApi])

  const resetColumnState = useCallback(() => {
    if (!gridApi) return
    gridApi.resetColumnState()
    gridApi.setFilterModel(null)
  }, [gridApi])

  const onSortedColumnsStateChanged = useCallback(
    (e: { api: GridApi }) => {
      const sortedColumns = e.api
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => {
          return {
            ...e.api.getColumnDef(col.colId),
            colId: col.colId,
          }
        })
        .filter(v => !!v) as ColDef[]
      e.api.setGridOption('suppressRowDrag', 0 < sortedColumns.length)

      const columnState = gridApi?.getColumnState()
      const sortedColumnState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedColumnState[state.colId] = state
          }
        })
      const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
        return {
          colId: col.colId,
          field: col.field,
          headerName: col.headerName,
          sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
        }
      })

      setSortColumnsState(sortedColumnsState)
    },
    [gridApi]
  )

  const onColumnVisible = useCallback(
    (e: ColumnVisibleEvent) => {
      rememberColumnState()
      onSortedColumnsStateChanged(e)
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api.getFilterModel()
      delete filterModel['uuid']
      bulkSheetState.saveFilterState(filterModel)
      setFilteredColumns(
        Object.keys(filterModel)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v && v.field !== 'uuid') as ColDef[]
      )
    },
    [bulkSheetState]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridApi) return
      const filterModel = gridApi.getFilterModel()
      delete filterModel[column.colId || column.field || '']
      gridApi.setFilterModel(filterModel)
    },
    [gridApi]
  )

  const resetFilters = useCallback(() => {
    if (!gridApi) return
    gridApi.setFilterModel(null)
  }, [gridApi])

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      rememberColumnState()
      onSortedColumnsStateChanged(e)
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<any>) => {
      gridApi?.applyColumnState({
        state: [{ colId: colId.toString(), sort: null }],
      })
    },
    [gridApi]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    gridApi?.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridApi])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      gridApi?.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridApi]
  )

  return {
    filteredColumns,
    sortColumnsState,
    rememberColumnState,
    resetColumnState,
    onColumnVisible,
    onFilterChanged,
    onDeleteFilteredColumn,
    resetFilters,
    onSortChanged,
    onDeleteSortedColumn,
    onDeleteSortedAllColumns,
    onChangeSortColumnState,
  }
}
